/**
 * P3-36/37: SpaceTab —— Space tab 组装根（迁移自
 * com.nuclearunicorn.game.ui.tab.SpaceTab，js/space.js:1499-1603）。
 *
 * 结构逐一对齐：
 * - 根 div.tabInner.Space；
 * - 右上角 "Hide complete missions" 复选框（id=toggleResearched），直接读写
 *   game.space.hideResearched；
 * - padding div（height:20px）；
 * - Ground Control SpacePanel：渲染所有 SpaceProgramBtn；
 * - Planet SpacePanel × N：每个 unlocked planet，title 含 cycle glyph（numerology
 *   perk）与 ETA；内部渲染 PlanetBuildingBtn；
 * - Rorshach SpacePanel：rorschachMission.on 时显示。
 */
import { useCallback, useMemo } from "react";
import { getGame } from "../../bridge/GameBridge";
import type { GamePage } from "../../bridge/GameBridge";
import { t } from "../../bridge/i18n";
import { useGameTick } from "../../bridge/useGameTick";
import { TooltipProvider } from "../../components/Tooltip/TooltipProvider";
import { PlanetBuildingBtn } from "./PlanetBuildingBtn";
import { SpacePanel } from "./SpacePanel";
import { SpaceProgramBtn } from "./SpaceProgramBtn";

interface PlanetMeta {
  name: string;
  label: string;
  unlocked: boolean;
  reached?: boolean;
  routeDays?: number;
  buildings: { name: string }[];
}

interface SpaceSnapshot {
  hideResearched: boolean;
  programs: string[];
  planets: PlanetMeta[];
  showRorshach: boolean;
}

function computeSnapshot(game: GamePage | null): SpaceSnapshot | null {
  if (!game) {
    return null;
  }
  const space = game.space as {
    hideResearched: boolean;
    programs: { name: string }[];
    planets: PlanetMeta[];
    getProgram: (name: string) => { on?: number } | null;
  };
  return {
    hideResearched: space.hideResearched,
    programs: space.programs.map((p) => p.name),
    planets: space.planets,
    showRorshach: space.getProgram("rorschachMission")?.on === 1,
  };
}

function snapshotEq(a: SpaceSnapshot | null, b: SpaceSnapshot | null): boolean {
  if (a === b) {
    return true;
  }
  if (!a || !b) {
    return false;
  }
  if (
    a.hideResearched !== b.hideResearched ||
    a.showRorshach !== b.showRorshach ||
    a.programs.length !== b.programs.length
  ) {
    return false;
  }
  for (let i = 0; i < a.programs.length; i++) {
    if (a.programs[i] !== b.programs[i]) {
      return false;
    }
  }
  if (a.planets.length !== b.planets.length) {
    return false;
  }
  for (let i = 0; i < a.planets.length; i++) {
    const ap = a.planets[i];
    const bp = b.planets[i];
    if (
      ap.name !== bp.name ||
      ap.label !== bp.label ||
      ap.unlocked !== bp.unlocked ||
      ap.buildings.length !== bp.buildings.length
    ) {
      return false;
    }
    for (let j = 0; j < ap.buildings.length; j++) {
      if (ap.buildings[j].name !== bp.buildings[j].name) {
        return false;
      }
    }
  }
  return true;
}

interface PlanetPanelProps {
  planet: PlanetMeta;
}

function PlanetPanelTitle({ planet }: PlanetPanelProps) {
  const title = useGameTick(
    useCallback((game: GamePage | null): string => {
      if (!game) {
        return planet.label;
      }

      // space.js:1559-1569：numerology perk 时前置 cycle glyph
      const prestige = game.prestige as {
        getPerk?: (name: string) => { researched?: boolean } | null;
      };
      const hasNumerology = prestige.getPerk?.("numerology")?.researched ?? false;
      let prefix = "";
      if (hasNumerology) {
        const calendar = game.calendar as { cycles?: { name: string; glyph: string }[] };
        const cycles = calendar.cycles ?? [];
        for (const cycle of cycles) {
          if (cycle.name === planet.name || (planet.name === "moon" && cycle.name === "redmoon")) {
            prefix += cycle.glyph + " ";
          }
        }
      }

      // space.js:1473-1477：未到达且 routeDays>0 时追加 ETA
      if (
        !planet.reached &&
        planet.unlocked &&
        (planet.routeDays ?? 0) > 0
      ) {
        const routeSpeed = Math.max(
          (game as { getEffect?: (name: string) => number }).getEffect?.("routeSpeed") ?? 1,
          1,
        );
        const displayDays = (game as { toDisplayDays?: (days: number) => string }).toDisplayDays?.(
          Math.round((planet.routeDays ?? 0) / routeSpeed),
        );
        return prefix + planet.label + " | ETA: " + displayDays;
      }
      return prefix + planet.label;
    }, [planet]),
    (a, b) => a === b,
  );

  return <>{title}</>;
}

function PlanetPanelContent({ planet }: PlanetPanelProps) {
  const buttons = useMemo(
    () => planet.buildings.map((bld) => <PlanetBuildingBtn key={bld.name} buildingName={bld.name} />),
    [planet.buildings],
  );
  return <>{buttons}</>;
}

export default function SpaceTab() {
  const snap = useGameTick(
    useCallback((game: GamePage | null) => computeSnapshot(game), []),
    snapshotEq,
  );

  const toggleHideResearched = useCallback(() => {
    const game = getGame();
    if (!game) {
      return;
    }
    const space = game.space as { hideResearched: boolean };
    space.hideResearched = !space.hideResearched;
  }, []);

  const programButtons = useMemo(
    () => snap?.programs.map((name) => <SpaceProgramBtn key={name} programName={name} />),
    [snap?.programs],
  );

  const planetPanels = useMemo(
    () =>
      snap?.planets
        .filter((planet) => planet.unlocked)
        .map((planet) => (
          <SpacePanel key={planet.name} title={<PlanetPanelTitle planet={planet} />}>
            <PlanetPanelContent planet={planet} />
          </SpacePanel>
        )),
    [snap?.planets],
  );

  if (!snap) {
    return null;
  }

  return (
    <TooltipProvider>
      <div className="tabInner Space">
        <div style={{ float: "right" }}>
          <input
            id="toggleResearched"
            type="checkbox"
            checked={snap.hideResearched}
            onChange={toggleHideResearched}
          />
          <label htmlFor="toggleResearched">{t("space.tab.hide.complete.missions")}</label>
        </div>
        <div style={{ height: "20px" }} />
        <SpacePanel title={t("space.ground.control.label")}>{programButtons}</SpacePanel>
        {planetPanels}
        {snap.showRorshach && (
          <SpacePanel title={t("space.panel.rorshach.label")}>
            <div />
          </SpacePanel>
        )}
      </div>
    </TooltipProvider>
  );
}
